import { addDaysToKey } from './business-date';

/**
 * Extending probation: which new end dates the dialog offers.
 *
 * The same rule the decide route enforces — the server is the authority; this
 * only keeps the dialog from proposing a date it would refuse.
 */

/** Unless HR picks another date, an extension runs three months more. */
export const DEFAULT_EXTENSION_MONTHS = 3;

/** One extension reaches at most another full probation term past the current end. */
export const MAX_EXTENSION_MONTHS = 6;

/** How far ahead of the end date the review falls due. */
export const REVIEW_LEAD_DAYS = 14;

/**
 * `YYYY-MM-DD`, `months` on — the same day of the month, or the target
 * month's last day when it is too short (31 Aug + 6 months is 28 Feb).
 */
export function addMonthsToKey(key: string, months: number): string {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  const targetMonth = month - 1 + months;
  const lastDay = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, targetMonth, Math.min(day, lastDay))).toISOString().slice(0, 10);
}

export interface ExtensionBounds {
  /** What the dialog proposes: the current end plus three months. */
  suggested: string;
  /** The earliest acceptable new end: after the current end, and after today. */
  min: string;
  /** The latest: six months past the current end. */
  max: string;
}

export function extensionBounds(currentEnd: string, today: string): ExtensionBounds {
  const afterEnd = addDaysToKey(currentEnd, 1);
  const afterToday = addDaysToKey(today, 1);
  const min = afterEnd > afterToday ? afterEnd : afterToday;
  const max = addMonthsToKey(currentEnd, MAX_EXTENSION_MONTHS);
  const proposed = addMonthsToKey(currentEnd, DEFAULT_EXTENSION_MONTHS);
  return { suggested: proposed < min ? min : proposed, min, max };
}

/** Why a chosen new end date would be refused, or null when it is acceptable. */
export function extensionError(
  value: string,
  currentEnd: string,
  today: string,
  formatDate: (key: string) => string,
): string | null {
  if (!value) return 'Choose the new last day of probation.';
  const { max } = extensionBounds(currentEnd, today);
  if (value <= currentEnd) {
    return `Choose a date after the current end, ${formatDate(currentEnd)}.`;
  }
  if (value <= today) return 'Choose a date still ahead.';
  if (value > max) {
    return `An extension runs at most ${MAX_EXTENSION_MONTHS} months: no later than ${formatDate(max)}.`;
  }
  return null;
}

/** The review falls due a fortnight before the (new) end of probation. */
export function reviewDueFor(probationEnd: string): string {
  return addDaysToKey(probationEnd, -REVIEW_LEAD_DAYS);
}
